/**
 * 權限操作監控儀表板
 * 只有超級管理員可以存取
 */

import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Alert, Badge, Table, 
  Form, Button, Spinner, ProgressBar, ListGroup 
} from 'react-bootstrap';
import { PermissionGate } from './components/PermissionGate';
import axios from './utils/axiosConfig';

const PermissionMonitor = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [health, setHealth] = useState(null);
  const [filters, setFilters] = useState({
    timeRange: '24h',
    userId: '',
    action: '',
    resource: ''
  });

  const token = localStorage.getItem('token');

  // 獲取權限日誌
  const fetchLogs = async () => {
    try {
      const response = await axios.get('/permission-monitor/logs', {
        params: filters,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setLogs(response.data.data);
      }
    } catch (error) {
      console.error('獲取權限日誌失敗:', error);
    }
  };

  // 獲取統計數據
  const fetchStats = async () => {
    try {
      const response = await axios.get('/permission-monitor/stats', {
        params: { timeRange: filters.timeRange },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('獲取統計數據失敗:', error);
    }
  };

  // 獲取警告
  const fetchAlerts = async () => {
    try {
      const response = await axios.get('/permission-monitor/alerts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAlerts(response.data.data);
      }
    } catch (error) {
      console.error('獲取警告失敗:', error);
    }
  };

  // 獲取系統健康狀況
  const fetchHealth = async () => {
    try {
      const response = await axios.get('/permission-monitor/health', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setHealth(response.data.data);
      }
    } catch (error) {
      console.error('獲取健康狀況失敗:', error);
    }
  };

  // 刷新所有數據
  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLogs(),
        fetchStats(),
        fetchAlerts(),
        fetchHealth()
      ]);
    } finally {
      setLoading(false);
    }
  };

  // 生成測試數據
  const generateTestData = async () => {
    try {
      const response = await axios.post('/permission-monitor/generate-test-data', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        alert('✅ 成功生成測試數據！');
        // 刷新數據
        await refreshAll();
      }
    } catch (error) {
      console.error('生成測試數據失敗:', error);
      alert('❌ 生成測試數據失敗: ' + (error.response?.data?.message || error.message));
    }
  };

  // 初始載入
  useEffect(() => {
    refreshAll();
    
    // 設定自動刷新
    const interval = setInterval(refreshAll, 30000); // 30秒刷新一次
    return () => clearInterval(interval);
  }, [filters]);

  // 處理篩選器變更
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <PermissionGate role="super_admin" showError={true}>
      <Container fluid className="py-4">
        {/* 頁面標題和控制 */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <h3>🔐 權限操作監控儀表板</h3>
              <div>
                <Button 
                  variant="outline-success" 
                  onClick={generateTestData}
                  disabled={loading}
                  className="me-2"
                >
                  🧪 生成測試數據
                </Button>
                <Button 
                  variant="outline-primary" 
                  onClick={refreshAll}
                  disabled={loading}
                >
                  {loading && <Spinner size="sm" className="me-2" />}
                  🔄 刷新數據
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* 系統健康狀況 */}
        {health && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">🏥 系統健康狀況</h6>
                  <Badge bg={
                    health.status === 'healthy' ? 'success' : 
                    health.status === 'degraded' ? 'warning' : 'danger'
                  }>
                    {health.status}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="mb-1">{health.score}%</h4>
                        <small className="text-muted">健康分數</small>
                        <ProgressBar 
                          now={health.score} 
                          variant={health.score >= 80 ? 'success' : health.score >= 60 ? 'warning' : 'danger'}
                          className="mt-2"
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <h6>檢查項目</h6>
                      {Object.entries(health.checks).map(([key, check]) => (
                        <div key={key} className="d-flex align-items-center mb-1">
                          <Badge 
                            bg={check.status === 'ok' ? 'success' : check.status === 'warning' ? 'warning' : 'danger'}
                            className="me-2"
                          >
                            {check.status}
                          </Badge>
                          <small>{check.message}</small>
                        </div>
                      ))}
                    </Col>
                    <Col md={5}>
                      {health.recommendations.length > 0 && (
                        <>
                          <h6>建議</h6>
                          <ListGroup variant="flush">
                            {health.recommendations.map((rec, idx) => (
                              <ListGroup.Item key={idx} className="px-0 py-1">
                                <small className="text-warning">⚠️ {rec}</small>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* 警告面板 */}
        {alerts.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">⚠️ 安全警告 ({alerts.length})</h6>
                </Card.Header>
                <Card.Body>
                  {alerts.map((alert, idx) => (
                    <Alert 
                      key={idx}
                      variant={
                        alert.severity === 'high' ? 'danger' :
                        alert.severity === 'medium' ? 'warning' : 'info'
                      }
                      className="mb-2"
                    >
                      <strong>{alert.type}:</strong> {alert.message}
                      <small className="d-block text-muted mt-1">
                        {new Date(alert.timestamp).toLocaleString()}
                      </small>
                    </Alert>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* 統計面板 */}
        {stats && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h3 className="text-primary">{stats.totalOperations}</h3>
                  <p className="mb-0">總操作次數</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h3 className="text-success">{stats.uniqueUsers}</h3>
                  <p className="mb-0">活躍用戶</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">📊 操作分佈</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col>
                      <h6>按角色</h6>
                      {Object.entries(stats.byRole).map(([role, count]) => (
                        <div key={role} className="d-flex justify-content-between">
                          <span>{role}</span>
                          <Badge bg="primary">{count}</Badge>
                        </div>
                      ))}
                    </Col>
                    <Col>
                      <h6>按動作</h6>
                      {Object.entries(stats.byAction).slice(0, 5).map(([action, count]) => (
                        <div key={action} className="d-flex justify-content-between">
                          <span>{action}</span>
                          <Badge bg="info">{count}</Badge>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* 篩選器 */}
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body>
                <Row>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>時間範圍</Form.Label>
                      <Form.Select 
                        value={filters.timeRange}
                        onChange={(e) => handleFilterChange('timeRange', e.target.value)}
                      >
                        <option value="1h">過去1小時</option>
                        <option value="24h">過去24小時</option>
                        <option value="7d">過去7天</option>
                        <option value="30d">過去30天</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>用戶ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="輸入用戶ID"
                        value={filters.userId}
                        onChange={(e) => handleFilterChange('userId', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>動作</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="篩選動作"
                        value={filters.action}
                        onChange={(e) => handleFilterChange('action', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>資源</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="篩選資源"
                        value={filters.resource}
                        onChange={(e) => handleFilterChange('resource', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setFilters({
                        timeRange: '24h',
                        userId: '',
                        action: '',
                        resource: ''
                      })}
                    >
                      清除篩選
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 權限日誌表格 */}
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h6 className="mb-0">📋 權限操作日誌 ({logs.length})</h6>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2">載入中...</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Table striped hover size="sm">
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                        <tr>
                          <th>時間</th>
                          <th>用戶</th>
                          <th>角色</th>
                          <th>動作</th>
                          <th>資源</th>
                          <th>詳情</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, idx) => (
                          <tr key={log.id || idx}>
                            <td>
                              <small>{new Date(log.timestamp).toLocaleString()}</small>
                            </td>
                            <td>
                              <div>
                                <strong>{log.userName}</strong>
                                <br />
                                <small className="text-muted">ID: {log.userId}</small>
                              </div>
                            </td>
                            <td>
                              <Badge bg={
                                log.userRole === 'super_admin' ? 'danger' :
                                log.userRole === 'secretary' ? 'success' :
                                log.userRole === 'member' ? 'primary' : 'secondary'
                              }>
                                {log.userRole}
                              </Badge>
                            </td>
                            <td>
                              <code>{log.action}</code>
                            </td>
                            <td>
                              <code>{log.resource}</code>
                            </td>
                            <td>
                              {Object.keys(log.details).length > 0 && (
                                <details>
                                  <summary style={{ cursor: 'pointer' }}>
                                    <small>詳情</small>
                                  </summary>
                                  <pre style={{ fontSize: '0.7rem', maxWidth: '200px', overflow: 'auto' }}>
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    
                    {logs.length === 0 && (
                      <div className="text-center py-4 text-muted">
                        沒有找到符合條件的日誌記錄
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 活躍用戶統計 */}
        {stats && stats.topUsers.length > 0 && (
          <Row className="mt-4">
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">🏆 活躍用戶 (Top 10)</h6>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {stats.topUsers.map((user, idx) => (
                      <ListGroup.Item 
                        key={idx} 
                        className="d-flex justify-content-between align-items-center px-0"
                      >
                        <span>
                          <Badge bg="secondary" className="me-2">#{idx + 1}</Badge>
                          {user.user}
                        </span>
                        <Badge bg="primary">{user.count} 次操作</Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">⏰ 24小時操作分佈</h6>
                </Card.Header>
                <Card.Body>
                  <div style={{ height: '200px', overflowY: 'auto' }}>
                    {stats.byHour.map((count, hour) => (
                      <div key={hour} className="d-flex align-items-center mb-1">
                        <span style={{ minWidth: '60px' }}>{hour}:00</span>
                        <div className="flex-grow-1 mx-2">
                          <ProgressBar 
                            now={count} 
                            max={Math.max(...stats.byHour)} 
                            variant="info"
                            style={{ height: '8px' }}
                          />
                        </div>
                        <span style={{ minWidth: '40px', textAlign: 'right' }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </PermissionGate>
  );
};

export default PermissionMonitor;